import React, { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarController,
  BarElement,
  Tooltip,
} from 'chart.js';
import moment from 'moment';
import { getChartTooltipStyle } from '../utils/chartDefaults';

// Matches the codebase's other bar charts (DailyScoreChart, LeaderBoardChart):
// react-chartjs-2 <Bar> with maintainAspectRatio:false inside a sized
// container, so Chart.js sizes the canvas to the container via ResizeObserver
// (no manual SVG sizing, no aspect-ratio runaway). Global Inter font comes
// from src/utils/chartDefaults.js (imported in index.js).
ChartJS.register(CategoryScale, LinearScale, BarController, BarElement, Tooltip);

const isDark = () =>
  typeof document !== 'undefined' &&
  document.documentElement.classList.contains('dark');

// Daily submission counts for the admin Platform overview. Gap-fills missing
// days as zero so the time axis stays uniform. Expects the
// `/market/challenge/submission/activity/` payload:
//   { start_date, end_date, interval, activity: [{ date, submission_count }] }
function SubmissionsActivityChart({ data }) {
  const dark = isDark();

  const days = useMemo(() => {
    if (!data || !data.start_date || !data.end_date) return [];
    const counts = {};
    (data.activity || []).forEach((row) => {
      counts[moment(row.date).format('YYYY-MM-DD')] = row.submission_count;
    });
    const out = [];
    const cursor = moment(data.start_date);
    const end = moment(data.end_date);
    let guard = 0; // guard against pathological ranges
    while (cursor.isSameOrBefore(end, 'day') && guard < 1000) {
      const key = cursor.format('YYYY-MM-DD');
      out.push({ key, label: cursor.format('MMM D'), count: counts[key] || 0 });
      cursor.add(1, 'day');
      guard += 1;
    }
    return out;
  }, [data]);

  const total = useMemo(() => days.reduce((s, d) => s + d.count, 0), [days]);

  const chartData = useMemo(() => ({
    labels: days.map((d) => d.label),
    datasets: [
      {
        label: 'Submissions',
        data: days.map((d) => d.count),
        // brand-600 (light) / brand-400 (dark)
        backgroundColor: dark ? 'rgba(58, 171, 184, 0.85)' : 'rgba(5, 120, 138, 0.85)',
        borderRadius: 1,
        categoryPercentage: 1.0,
        barPercentage: 0.9,
      },
    ],
  }), [days, dark]);

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        ...getChartTooltipStyle(dark),
        callbacks: {
          title: (items) => days[items[0].dataIndex]?.label ?? '',
          label: (ctx) => {
            const v = ctx.parsed.y;
            return `${v} submission${v === 1 ? '' : 's'}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        border: { display: false },
        ticks: {
          color: dark ? '#94a3b8' : '#9ca3af',
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: 6,
          font: { size: 10 },
        },
      },
      y: {
        display: false,
        beginAtZero: true,
      },
    },
  }), [days, dark]);

  if (!days.length || total === 0) {
    return (
      <div className="h-full w-full flex items-center justify-center text-xs text-gray-400 dark:text-gray-500">
        No submissions in this period
      </div>
    );
  }

  return (
    <div className="h-full w-full">
      <Bar data={chartData} options={options} />
    </div>
  );
}

export default SubmissionsActivityChart;
